// =============================================================
// User API client — login, profile, bookings, change password.
// =============================================================
//
// Sends/receives JSON and uses the same `apiBase` resolver as the
// admin API. The session is an HttpOnly cookie set by the server
// (it travels automatically with `credentials: 'same-origin'`),
// so we don't store a token ourselves. The user object is cached
// in localStorage so the UI can show the logged-in state without
// a round-trip on every page render.

import type { UserSession, Booking, AgendaEvent, SessionType } from './content';

function apiBase(): string {
  const fromMeta = document.querySelector('meta[name="api-base"]')?.getAttribute('content');
  return (fromMeta || '').replace(/\/$/, '');
}

const USER_KEY = 'studio333:user';
const URL_PATH = (): string => (typeof location !== 'undefined' ? location.pathname : '/');

/** Read the cached user (or null) out of localStorage. */
export function getCachedUser(): UserSession | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UserSession;
  } catch { return null; }
}

function writeCachedUser(u: UserSession | null): void {
  try {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
  } catch { /* noop */ }
}

async function http<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'accept': 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  const res = await fetch(`${apiBase()}${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`${res.status} ${res.statusText}: ${text}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

// ---- Auth ----
export async function login(email: string, password: string): Promise<UserSession> {
  const out = await http<{ ok: true; user: UserSession }>('POST', '/api/auth/login', { email, password });
  writeCachedUser(out.user);
  return out.user;
}

export async function logout(): Promise<void> {
  try { await http<{ ok: true }>('POST', '/api/auth/logout'); } catch { /* noop */ }
  writeCachedUser(null);
  // Recover the locale from the path before redirecting to /login.
  const m = /^\/(hu|en)(?=\/|$)/.exec(URL_PATH());
  const locale = m?.[1] ?? 'hu';
  location.href = `/${locale}/login`;
}

export async function fetchMe(): Promise<UserSession | null> {
  try {
    const out = await http<{ user: UserSession }>('GET', '/api/auth/me');
    writeCachedUser(out.user);
    return out.user;
  } catch (err) {
    if ((err as { status?: number }).status === 401) {
      writeCachedUser(null);
      return null;
    }
    throw err;
  }
}

export async function changePassword(opts: { currentPassword?: string; newPassword: string }): Promise<UserSession> {
  const out = await http<{ ok: true; passwordChangeRequired: boolean }>(
    'POST', '/api/auth/change-password', opts,
  );
  const cached = getCachedUser();
  if (cached) {
    cached.passwordChangeRequired = !!out.passwordChangeRequired;
    writeCachedUser(cached);
    return cached;
  }
  // Fallback: re-fetch.
  return (await fetchMe())!;
}

export async function updateProfile(patch: Partial<UserSession>): Promise<UserSession> {
  const out = await http<{ ok: true; user: UserSession }>('PATCH', '/api/me', patch);
  writeCachedUser(out.user);
  return out.user;
}

// ---- Bookings ----
export async function fetchMyBookings(): Promise<Booking[]> {
  const out = await http<{ bookings: Booking[] }>('GET', '/api/bookings');
  return out.bookings;
}

export async function createBooking(opts: {
  location: 'A22' | 'M15';
  startsAt: string;
  eventId?: string | null;
  /** Optional FK to a session_types row. The server rejects the request
   *  if the type's discipline doesn't match the caller's discipline. */
  sessionTypeId?: string | null;
  notes?: string;
  recurrenceCount?: number;
  recurrenceUntil?: string | null;
  recurrenceWeekdays?: number[] | null;
}): Promise<{ ok: true; bookingId: string }> {
  return http<{ ok: true; bookingId: string }>('POST', '/api/bookings', opts);
}

export async function cancelBooking(id: string): Promise<void> {
  await http<{ ok: true }>('DELETE', `/api/bookings/${id}`);
}

// ---- Session types (trainee-facing) ----
//
// Fetches the public /api/session-types list and filters client-side
// to the member's discipline + active flag. Used by the booking modal
// to populate the session-type dropdown.
export async function fetchSessionTypesForMember(): Promise<SessionType[]> {
  const cached = getCachedUser();
  const discipline: 'strength' | 'yoga' = cached?.discipline ?? 'strength';
  let raw: SessionType[] = [];
  try {
    const out = await http<{ sessionTypes: SessionType[] }>('GET', '/api/session-types');
    raw = out.sessionTypes ?? [];
  } catch {
    raw = [];
  }
  return raw
    .filter(s => s.discipline === discipline && s.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.hu.localeCompare(b.name.hu));
}

// ---- Agenda events (public read) ----
export async function fetchAgendaEvents(): Promise<AgendaEvent[]> {
  try {
    return await http<AgendaEvent[]>('GET', '/api/agenda');
  } catch {
    return [];
  }
}

// ---- Taken slots (public read) ----
//
// Approved trainee bookings that need to be visible on the calendar
// even to users who don't own them. Without this, approved slots
// would appear as "open" on every other trainee's agenda and on the
// public agenda — leading to double-bookings.
//
// Returned objects are shape-compatible with AgendaEvent (kind =
// 'taken'), with one extra `source: 'booking'` marker so the user
// agenda can give the caller's own bookings priority over this list.
export async function fetchTakenSlots(): Promise<AgendaEvent[]> {
  try {
    return await http<AgendaEvent[]>('GET', '/api/agenda/availability');
  } catch {
    return [];
  }
}
